import {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react"
import { initializeApp } from "firebase/app"
import { getAnalytics } from "firebase/analytics"
import { getDatabase, onValue, ref } from "firebase/database"
import { logger, Status } from "../services/utilities/utility"
import { getUser } from "../services/server/auth"
import { FetchUserAttributesOutput } from "aws-amplify/auth"
import { usePostHogIdentify } from "../services/posthog/posthog.hooks"
import { CmdPressedProvider } from "./cmdPressed"

export interface PanelStatusItem {
  name: string
  status: string
  loading: boolean
  updateFunction?: () => void
  errorCode?: string
}

// firebase configuration
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: "aworld-solar.firebaseapp.com",
  projectId: "aworld-solar",
  storageBucket: "aworld-solar.appspot.com",
  messagingSenderId: "952423226173",
  appId: "1:952423226173:web:c3382765f0b523daba0354",
  measurementId: "G-P14855SKY9",
  databaseURL:
    "https://aworld-solar-default-rtdb.europe-west1.firebasedatabase.app/",
}

// firebase initialize
export const firebaseApp = initializeApp(firebaseConfig)
getAnalytics(firebaseApp)

// firebase realtime database initialize
const database = getDatabase(firebaseApp)

interface MainContextInterface {
  drawerOpen: boolean
  toggleDrawer: () => void
  isAuth: any
  setIsAuth: Dispatch<SetStateAction<any>>
  error: boolean
  setError: Dispatch<SetStateAction<boolean>>
  errorMessage: string
  setErrorMessage: Dispatch<SetStateAction<string>>
  animation: boolean
  setAnimation: Dispatch<SetStateAction<boolean>>
  changesSaved: boolean
  setChangesSaved: Dispatch<SetStateAction<boolean>>
  panelStatus: PanelStatusItem[]
  setPanelStatus: Dispatch<SetStateAction<PanelStatusItem[]>>
  updatePanelStatus: (name: string, patch: Partial<PanelStatusItem>) => void
  showPadding: string
  setShowPadding: Dispatch<SetStateAction<string>>
  showAdvancedOption: string
  setShowAdvancedOption: Dispatch<SetStateAction<string>>
  manager: {
    forceUpdate: boolean
    maintenance: boolean
    version: string
    lastUpdate: string
    phase: "deploying" | "deployed"
  }
  ignoreManager: boolean
  setIgnoreManager: Dispatch<SetStateAction<boolean>>
  clipboardFeedbackOpen: boolean
  setClipboardFeedbackOpen: Dispatch<SetStateAction<boolean>>
  user: FetchUserAttributesOutput | null
}

const MainContext = createContext<MainContextInterface>({
  drawerOpen: true,
  toggleDrawer: () => {},
  isAuth: undefined,
  setIsAuth: () => {},
  error: false,
  setError: () => {},
  errorMessage: "",
  setErrorMessage: () => {},
  animation: true,
  setAnimation: () => {},
  changesSaved: false,
  setChangesSaved: () => {},
  panelStatus: [],
  setPanelStatus: () => {},
  updatePanelStatus: () => {},
  showPadding: "",
  setShowPadding: () => {},
  showAdvancedOption: "",
  setShowAdvancedOption: () => {},
  manager: {
    forceUpdate: false,
    maintenance: false,
    version: import.meta.env.VITE_VERSION,
    lastUpdate: "",
    phase: "deployed",
  },
  ignoreManager: false,
  setIgnoreManager: () => {},
  clipboardFeedbackOpen: false,
  setClipboardFeedbackOpen: () => {},
  user: null,
})

const MainController = ({ children }: { children: ReactNode }) => {
  const [drawerOpen, setDrawerOpen] = useState<boolean>(
    localStorage.getItem("drawerOpen")
      ? localStorage.getItem("drawerOpen") === "true"
      : true
  ) // left drawer expanded or not
  const toggleDrawer = useCallback(() => {
    setDrawerOpen((prev) => {
      localStorage.setItem("drawerOpen", (!prev).toString())
      return !prev
    })
  }, [])
  const [isAuth, setIsAuth] = useState<any>() // is user authenticated or not
  const [error, setError] = useState<boolean>(false) // is there an error or not
  const [errorMessage, setErrorMessage] = useState<string>("") // error message
  const [animation, setAnimation] = useState(true) // for enter and exit animations
  const [changesSaved, setChangesSaved] = useState<boolean>(false) // changes saved snackbar
  const [clipboardFeedbackOpen, setClipboardFeedbackOpen] =
    useState<boolean>(false) // copied to clipboard snackbar
  const [user, setUser] = useState<FetchUserAttributesOutput | null>(null)

  // make error snackbar disappear after 5 seconds
  useEffect(() => {
    if (!error) return
    const id = setTimeout(() => setError(false), 5000)
    return () => clearTimeout(id)
  }, [error])

  // make save changes snackbar disappear after 4 seconds
  useEffect(() => {
    if (!changesSaved) return
    const id = setTimeout(() => setChangesSaved(false), 4000)
    return () => clearTimeout(id)
  }, [changesSaved])

  // panel status managment
  const [panelStatus, setPanelStatus] = useState<PanelStatusItem[]>([
    {
      name: "Topics",
      status: "loading",
      loading: false,
    },
    {
      name: "Main Topics",
      status: "loading",
      loading: false,
    },
    {
      name: "SDGs",
      status: "loading",
      loading: false,
    },
    {
      name: "SDG targets",
      status: "loading",
      loading: false,
    },
    {
      name: "Media tags",
      status: "loading",
      loading: false,
    },
    {
      name: "Regions",
      status: "loading",
      loading: false,
    },
    {
      name: "SubRegions",
      status: "loading",
      loading: false,
    },
    {
      name: "Countries",
      status: "loading",
      loading: false,
    },
    {
      name: "Categories",
      status: "loading",
      loading: false,
    },
    {
      name: "Main Categories",
      status: "loading",
      loading: false,
    },
    {
      name: "Metrics",
      status: "loading",
      loading: false,
    },
    {
      name: "Footprint Questions",
      status: "loading",
      loading: false,
    },
    {
      name: "Media Categories",
      status: "loading",
      loading: false,
    },
    {
      name: "Channels",
      status: "loading",
      loading: false,
    },
    {
      name: "Enrichers",
      status: "loading",
      loading: false,
    },
    { name: "Currencies", status: "loading", loading: false },
    { name: "Mission Types", status: "loading", loading: false },
    {
      name: "Episodes",
      status: "loading",
      loading: false,
    },
    {
      name: "Daily Episodes",
      status: "loading",
      loading: false,
    },
    {
      name: "Journeys",
      status: "loading",
      loading: false,
    },
    {
      name: "Media",
      status: "loading",
      loading: false,
    },
    {
      name: "Teams",
      status: "loading",
      loading: false,
    },
    {
      name: "Free release",
      status: "loading",
      loading: false,
    },
    {
      name: "News",
      status: "loading",
      loading: false,
    },
    {
      name: "Current News",
      status: "loading",
      loading: false,
    },
    {
      name: "Actions",
      status: "loading",
      loading: false,
    },
    {
      name: "Series",
      status: "loading",
      loading: false,
    },
    {
      name: "Badges",
      status: "loading",
      loading: false,
    },
    {
      name: "Challenges",
      status: "loading",
      loading: false,
    },
    {
      name: "Marketplace",
      status: "loading",
      loading: false,
    },
    {
      name: "NFT catalogs",
      status: "loading",
      loading: false,
    },
    {
      name: "Activation codes",
      status: "loading",
      loading: false,
    },
    {
      name: "Templates",
      status: "loading",
      loading: false,
    },
    {
      name: "Missions",
      status: "loading",
      loading: false,
    },
    {
      name: "Surveys",
      status: "loading",
      loading: false,
    },
    {
      name: "Check-ins",
      status: "loading",
      loading: false,
    },
    {
      name: "Check-in Groups",
      status: "loading",
      loading: false,
    },
    {
      name: "Leagues",
      status: "loading",
      loading: false,
    },
  ])

  // immutably patch a single panel's status entry by name
  const updatePanelStatus = useCallback(
    (name: string, patch: Partial<PanelStatusItem>) => {
      setPanelStatus((prev) =>
        prev.map((item) =>
          item.name === name ? { ...item, ...patch } : item
        )
      )
    },
    []
  )

  // show padding
  const [showPadding, setShowPadding] = useState<string>(
    localStorage.getItem("showPadding")
      ? localStorage.getItem("showPadding")
      : "yes"
  )

  useEffect(() => {
    localStorage.setItem("showPadding", showPadding)
  }, [showPadding])

  // show advanced options
  const [showAdvancedOption, setShowAdvancedOption] = useState<string>(
    localStorage.getItem("showAdvancedOption")
      ? localStorage.getItem("showAdvancedOption")
      : "no"
  )

  useEffect(() => {
    localStorage.setItem("showAdvancedOption", showAdvancedOption)
  }, [showAdvancedOption])

  // subscribe to firebase realtime database for version changes
  const [manager, setManager] = useState<{
    forceUpdate: boolean
    maintenance: boolean
    version: string
    lastUpdate: string
    phase: "deploying" | "deployed"
  }>({
    forceUpdate: false,
    maintenance: false,
    version: import.meta.env.VITE_VERSION,
    lastUpdate: "",
    phase: "deployed",
  })
  const [ignoreManager, setIgnoreManager] = useState<boolean>(false)

  useEffect(() => {
    const unsubscribe = onValue(
      ref(database, `solar-client/${import.meta.env.VITE_ENV}`),
      (snapshot) => {
        const data = snapshot.val()
        logger(Status.Important, "manager status", data)
        setIgnoreManager(false)
        setManager(data)
      }
    )
    return () => unsubscribe()
  }, [])

  // get current user
  useEffect(() => {
    const callGetUser = async () => {
      const currentUser = await getUser()
      setUser(currentUser)
    }

    if (isAuth) {
      callGetUser()
    } else {
      setUser(null)
    }
  }, [isAuth])

  // identify user in PostHog
  usePostHogIdentify({
    userId: user?.sub ?? null,
    email: user?.email ?? null,
  })

  const contextValue = useMemo<MainContextInterface>(
    () => ({
      drawerOpen,
      toggleDrawer,
      isAuth,
      setIsAuth,
      error,
      setError,
      errorMessage,
      setErrorMessage,
      animation,
      setAnimation,
      changesSaved,
      setChangesSaved,
      panelStatus,
      setPanelStatus,
      updatePanelStatus,
      showPadding,
      setShowPadding,
      showAdvancedOption,
      setShowAdvancedOption,
      manager,
      ignoreManager,
      setIgnoreManager,
      clipboardFeedbackOpen,
      setClipboardFeedbackOpen,
      user,
    }),
    [
      drawerOpen,
      toggleDrawer,
      isAuth,
      error,
      errorMessage,
      animation,
      changesSaved,
      panelStatus,
      updatePanelStatus,
      showPadding,
      showAdvancedOption,
      manager,
      ignoreManager,
      clipboardFeedbackOpen,
      user,
    ]
  )

  return (
    <MainContext.Provider value={contextValue}>
      <CmdPressedProvider>{children}</CmdPressedProvider>
    </MainContext.Provider>
  )
}

export { MainController, MainContext }
